import React, { Suspense } from "react";
import ProtectedRouteBoundary from "app/boundaries/ProtectedRouteBoundary";
import AuthorizedRoute from "app/boundaries/AuthorizedRoute";
import PageNotFound from "app/pages/PageNotFound";
import DashboardPageFallback from "app/routes/DashboardPageFallback";
import lazyWithRetry from "app/utils/lazyWithRetry";

/**
 * Wraps a route's element with Suspense, authorization (role), and protection (refresh token).
 * - protected: true → requires refresh token; no refresh → redirect to /login.
 * - authorized: [roles] → requires one of these roles; wrong role → "You are not authorized" page.
 * Order: Protected (token) wraps Authorized (role) wraps page so token is checked first.
 */
export const wrapRoutes = (routes = []) => {
  return routes.map((route) => {
    const { element, lazyComponent, authorized, protected: isProtected, ...rest } = route;

    let wrappedElement;

    if (lazyComponent && typeof lazyComponent === "function") {
      // All route-level lazy loads use lazyWithRetry (production-safe: single refresh per session on chunk failure)
      const LazyPage = lazyWithRetry(lazyComponent);
      wrappedElement = (
        <Suspense fallback={<DashboardPageFallback />}>
          <LazyPage />
        </Suspense>
      );
    } else if (element) {
      wrappedElement = <Suspense fallback={<DashboardPageFallback />}>{element}</Suspense>;
    } else {
      wrappedElement = (
        <PageNotFound
          statusCode={404}
          title=" "
          subTitle="Page not found"
          style={{ backgroundColor: "rgb(244, 247, 255)" }}
        />
      );
    }

    if (authorized && authorized.length > 0) {
      wrappedElement = <AuthorizedRoute authorized={authorized}>{wrappedElement}</AuthorizedRoute>;
    }

    if (isProtected) {
      wrappedElement = <ProtectedRouteBoundary>{wrappedElement}</ProtectedRouteBoundary>;
    }

    return {
      ...rest,
      element: wrappedElement,
    };
  });
};
